import { useRoute, useRouter } from 'vue-router';

import {
  DLQ_STATUS_VALUES,
  QUERY_KEYS,
  RULE_STATUS_VALUES,
} from '../utils/automationOperationsConstants';

/**
 * Deep-link state for the automation operations panel: hydrates the filters,
 * pagination and drill-down refs from the URL and writes the normalized state
 * back with `router.replace`. The refs are owned by the page and passed in, so
 * this composable never introduces new global state.
 */
export const useAutomationOperationsQuery = ({
  statusFilter,
  ruleFilters,
  ruleHealthMeta,
  dlqMeta,
  replayForm,
  drawerDeadLetterId,
}) => {
  const route = useRoute();
  const router = useRouter();

  const queryValue = value => (Array.isArray(value) ? value[0] : value);

  const positiveInteger = (value, fallback = 1) => {
    const parsed = Number.parseInt(queryValue(value), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  };

  const boundedWindowHours = value => {
    const parsed = positiveInteger(value, 24);
    return Math.min(Math.max(parsed, 1), 720);
  };

  const querySignature = query =>
    JSON.stringify(
      Object.keys(query || {})
        .sort()
        .map(key => [key, query[key]])
    );

  const routeQuery = () => route?.query || {};

  let lastWrittenQuerySignature = '';

  const setQueryValue = (query, key, value, defaultValue = '') => {
    const normalized =
      value === null || value === undefined ? '' : String(value);
    if (normalized === '' || normalized === String(defaultValue)) {
      delete query[key];
    } else {
      query[key] = normalized;
    }
  };

  const applyOperationsQuery = (query = routeQuery()) => {
    const dlqStatus = queryValue(query[QUERY_KEYS.dlqStatus]);
    statusFilter.value = DLQ_STATUS_VALUES.includes(dlqStatus)
      ? dlqStatus
      : 'open';

    ruleFilters.value = {
      q: queryValue(query[QUERY_KEYS.ruleQuery]) || '',
      eventName: queryValue(query[QUERY_KEYS.ruleEvent]) || '',
      status: RULE_STATUS_VALUES.includes(
        queryValue(query[QUERY_KEYS.ruleStatus])
      )
        ? queryValue(query[QUERY_KEYS.ruleStatus])
        : 'all',
      windowHours: boundedWindowHours(query[QUERY_KEYS.ruleWindow]),
    };

    ruleHealthMeta.value = {
      ...ruleHealthMeta.value,
      page: positiveInteger(query[QUERY_KEYS.rulePage], 1),
    };
    dlqMeta.value = {
      ...dlqMeta.value,
      page: positiveInteger(query[QUERY_KEYS.dlqPage], 1),
    };

    const executionId = queryValue(query[QUERY_KEYS.executionId]);
    const actionRef = queryValue(query[QUERY_KEYS.actionRef]);
    replayForm.value = {
      ...replayForm.value,
      executionId: executionId || '',
      actionRef: actionRef || '',
    };

    return {
      rulePage: ruleHealthMeta.value.page,
      dlqPage: dlqMeta.value.page,
      deadLetterId: positiveInteger(query[QUERY_KEYS.deadLetterId], 0),
      executionId: executionId || '',
    };
  };

  const replaceOperationsQuery = (overrides = {}) => {
    const nextQuery = { ...routeQuery() };
    const rulePage = overrides.rulePage ?? ruleHealthMeta.value.page;
    const dlqPage = overrides.dlqPage ?? dlqMeta.value.page;
    const deadLetterId =
      overrides.deadLetterId === undefined
        ? drawerDeadLetterId.value
        : overrides.deadLetterId;

    setQueryValue(nextQuery, QUERY_KEYS.dlqStatus, statusFilter.value, 'open');
    setQueryValue(nextQuery, QUERY_KEYS.dlqPage, dlqPage, 1);
    setQueryValue(nextQuery, QUERY_KEYS.deadLetterId, deadLetterId);
    setQueryValue(nextQuery, QUERY_KEYS.ruleQuery, ruleFilters.value.q.trim());
    setQueryValue(
      nextQuery,
      QUERY_KEYS.ruleEvent,
      ruleFilters.value.eventName.trim()
    );
    setQueryValue(
      nextQuery,
      QUERY_KEYS.ruleStatus,
      ruleFilters.value.status,
      'all'
    );
    setQueryValue(
      nextQuery,
      QUERY_KEYS.ruleWindow,
      ruleFilters.value.windowHours,
      24
    );
    setQueryValue(nextQuery, QUERY_KEYS.rulePage, rulePage, 1);
    setQueryValue(
      nextQuery,
      QUERY_KEYS.executionId,
      replayForm.value.executionId
    );
    setQueryValue(
      nextQuery,
      QUERY_KEYS.actionRef,
      replayForm.value.actionRef.trim()
    );

    const nextSignature = querySignature(nextQuery);
    if (nextSignature === querySignature(routeQuery())) return;

    lastWrittenQuerySignature = nextSignature;
    const result = router.replace({ query: nextQuery });
    result?.catch?.(() => {});
  };

  // The route watcher must ignore the write this panel just performed,
  // otherwise every `replaceOperationsQuery` would trigger a full refetch.
  const isSelfWrittenQuery = query => {
    const signature = querySignature(query || {});
    if (signature === lastWrittenQuerySignature) {
      lastWrittenQuerySignature = '';
      return true;
    }
    return false;
  };

  return {
    routeQuery,
    applyOperationsQuery,
    replaceOperationsQuery,
    isSelfWrittenQuery,
  };
};
